import { z } from 'zod';
import { DEFAULT_TASK_PRIORITY, taskPriorities, type TaskPriority } from '../enums.js';
import { dateOnlySchema } from './patients.js';

/**
 * Tâches personnelles : la liste de choses à faire d'un membre, dans le cabinet actif.
 *
 * **Chacun ne voit que les siennes**, propriétaire compris : c'est un pense-bête, pas un outil de
 * répartition du travail. Le serveur pose l'auteur d'après la session, jamais d'après le corps.
 *
 * Le libellé est un texte libre du praticien et peut nommer un patient ; le rattachement à un
 * dossier est facultatif, vérifié du même cabinet, et n'emporte aucune donnée clinique.
 */

export const TASK_LABEL_MAX_LENGTH = 200;
/** Description facultative, en texte brut : la V1 la portait sans borne, on la borne. */
export const TASK_DESCRIPTION_MAX_LENGTH = 1000;

/**
 * Plafond par membre et par cabinet. Une liste de choses à faire qui dépasse cinq cents lignes a
 * cessé d'en être une ; la borne existe pour qu'une boucle côté client ne remplisse pas la base.
 */
export const TASKS_PER_MEMBER_LIMIT = 500;

const taskLabel = z.string().trim().min(1).max(TASK_LABEL_MAX_LENGTH);
/** La chaîne vide devient `null` : une seule forme du vide en base. */
const taskDescription = z
  .string()
  .trim()
  .max(TASK_DESCRIPTION_MAX_LENGTH)
  .transform((value) => (value === '' ? null : value))
  .nullable();

export const taskCreateSchema = z
  .object({
    label: taskLabel,
    description: taskDescription.default(null),
    priority: z.enum(taskPriorities).default(DEFAULT_TASK_PRIORITY),
    dueDate: dateOnlySchema.nullable().default(null),
    patientId: z.uuid().nullable().default(null),
  })
  .strict();
export type TaskCreateInput = z.infer<typeof taskCreateSchema>;

/** Modification partielle. `done` coche ou décoche ; la date de réalisation est posée par le serveur. */
export const taskUpdateSchema = z
  .object({
    label: taskLabel,
    description: taskDescription,
    priority: z.enum(taskPriorities),
    dueDate: dateOnlySchema.nullable(),
    patientId: z.uuid().nullable(),
    done: z.boolean(),
  })
  .partial()
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'Aucun champ à modifier');
export type TaskUpdateInput = z.infer<typeof taskUpdateSchema>;

export const taskIdParamSchema = z.object({ id: z.uuid() }).strict();

/** Le dossier rattaché, tel que la liste l'affiche : de quoi le nommer et y mener, rien d'autre. */
export interface TaskPatientDto {
  id: string;
  firstName: string;
  lastName: string;
}

export interface TaskDto {
  id: string;
  label: string;
  description: string | null;
  priority: TaskPriority;
  dueDate: string | null;
  done: boolean;
  completedAt: string | null;
  createdAt: string;
  /** Nul sans rattachement, et aussi quand le dossier est en corbeille : il n'y a plus où mener. */
  patient: TaskPatientDto | null;
}

/** La liste entière, à faire d'abord puis par échéance. Bornée par `TASKS_PER_MEMBER_LIMIT`. */
export interface TaskListDto {
  items: TaskDto[];
}

export interface TaskClearCompletedDto {
  deleted: number;
}
